import {
  Block,
  Box,
  Card,
  NavList,
  Paragraph,
  SubscriberStarIcon,
} from '@deckee/deck-hand';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { selectHasCurrentMemberActiveSubscription } from '../../../../services/store/modules/backend-service';
import usePaywall from '../../../hooks/use-paywall';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import CheckSwitch from '../../../shared/_deprecated/components/CheckSwitch/CheckSwitch';
import { MarkerGroupIcon } from '../MapSettingsPage';
import BaseMapButton from './BaseMapButton/BaseMapButton';

const CardBodyNoPadding = styled(Card.Body)`
  padding-left: 8px;
`;

const StyledNavListItem = styled(NavList.Item)`
  * {
    overflow: visible !important;
  }
`;

const MapSettingsContainerView = (props) => {
  const {
    toggleSelectedLayerType,
    selectedLayerTypes,
    BASE_MAP_LAYER_DEFINITIONS,
    handleUpdateSelectedBaseMapLayer,
    selectedBaseMapLayer,
    mapSettingsMarkerGroups,
  } = props;

  const selectedLayerTypesSet = new Set(selectedLayerTypes);
  const history = useHistory();

  const hasActiveSubscription = useSelector(
    selectHasCurrentMemberActiveSubscription,
  );

  const { presentPaywall, hasSubscriptionFeaturesEnabled } =
    usePaywall('Map settings');

  const handleSelectMapLayer = (layer) => {
    const config = BASE_MAP_LAYER_DEFINITIONS[layer];
    if (config.subscriberOnly && !hasActiveSubscription) {
      presentPaywall();
    } else {
      handleUpdateSelectedBaseMapLayer(layer);
    }
  };

  const handleClickAction = (url) => {
    history.push(url);
  };

  return (
    <SheetLayout heading="Map settings" showMenuBar={false} showMapOverlay>
      <SheetBody>
        <Card>
          <Card.Heading fontWeight="bold" color="black">
            Map type
          </Card.Heading>
          <Card.Body display="flex" flexWrap="wrap">
            {Object.keys(BASE_MAP_LAYER_DEFINITIONS)
              // Only show types if the layer is meant to be available
              .filter((baseLayerType) => {
                const config = BASE_MAP_LAYER_DEFINITIONS[baseLayerType];
                return hasSubscriptionFeaturesEnabled
                  ? true
                  : !config.subscriberOnly;
              })
              .map((baseLayerType) => (
                <Box
                  key={BASE_MAP_LAYER_DEFINITIONS[baseLayerType].displayName}
                  mb="3"
                  flex="1 0 33%"
                >
                  <BaseMapButton
                    onClick={() => handleSelectMapLayer(baseLayerType)}
                    name={BASE_MAP_LAYER_DEFINITIONS[baseLayerType].displayName}
                    imageUrl={
                      BASE_MAP_LAYER_DEFINITIONS[baseLayerType].iconImage
                    }
                    active={selectedBaseMapLayer === baseLayerType}
                    subscriberOnly={
                      BASE_MAP_LAYER_DEFINITIONS[baseLayerType].subscriberOnly
                    }
                  />
                </Box>
              ))}
          </Card.Body>
          <Card.Heading fontWeight="bold" color="black">
            Layers
          </Card.Heading>
          <CardBodyNoPadding>
            <NavList>
              <NavList.Item>
                <IconWithTextLockup
                  flex="1 1 0px"
                  image={
                    <MarkerGroupIcon
                      bg="#7046E8"
                      icon={
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 21 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.99153 0V2.10394H7.02887C7.01565 2.10394 7.00242 2.10394 6.98919 2.10394C6.77096 2.12367 6.60398 2.30612 6.60564 2.52473V4.62868H4.48945C4.25634 4.62868 4.06622 4.81771 4.06622 5.04947V8.38948L2.14842 9.02066C1.2788 9.30831 0.872099 10.2995 1.22259 11.1115C1.22259 11.1164 1.22259 11.1197 1.22259 11.1246L2.7965 14.412V18.1334C2.26581 18.3783 1.60946 18.4753 1.18291 18.4753C0.926657 18.4753 0.696853 18.4358 0.429023 18.4358C0.276923 18.4342 0.134742 18.5131 0.0570379 18.6446C-0.0190126 18.7761 -0.0190126 18.9371 0.0570379 19.0686C0.134742 19.2001 0.276923 19.279 0.429023 19.2774C0.584431 19.2774 0.845647 19.3168 1.18291 19.3168C1.78801 19.3168 2.64936 19.141 3.39168 18.6988C3.42971 18.6824 3.46608 18.6594 3.49749 18.6331C3.5008 18.6314 3.50741 18.6347 3.51072 18.6331C4.27949 19.1344 5.1987 19.3168 5.83852 19.3168C6.47503 19.3168 7.39921 19.1278 8.16632 18.6331C8.93509 19.1344 9.85431 19.3168 10.4941 19.3168C11.1306 19.3168 12.0548 19.1278 12.8219 18.6331C13.5907 19.1344 14.5099 19.3168 15.1497 19.3168C15.7664 19.3168 16.6476 19.1327 17.3982 18.6725C17.4709 18.7136 17.5536 18.7317 17.6362 18.7251C18.3703 19.1525 19.2085 19.3168 19.8053 19.3168C20.1426 19.3168 20.4567 19.2675 20.7312 19.159C20.951 19.0719 21.0569 18.8254 20.9692 18.6068C20.8816 18.3881 20.6336 18.283 20.4137 18.3701C20.2649 18.4292 20.0616 18.4753 19.8053 18.4753C19.3308 18.4753 18.5852 18.3503 18.033 18.0413V14.3857L19.554 11.1246V11.1115C19.9591 10.288 19.4896 9.30667 18.6282 9.02066L16.7633 8.40263V5.04947C16.7633 4.81771 16.5732 4.62868 16.3401 4.62868H14.2239V2.52473C14.2239 2.29297 14.0338 2.10394 13.8007 2.10394H10.838V0H9.99153ZM7.45211 2.94552H13.3774V5.04947C13.3774 5.28123 13.5676 5.47026 13.8007 5.47026H15.9168V8.12649L10.547 6.33813C10.4792 6.31183 10.4065 6.30197 10.3354 6.31183C10.3172 6.32005 10.299 6.32827 10.2825 6.33813L4.91269 8.11334V5.47026H7.02887C7.26198 5.47026 7.45211 5.28123 7.45211 5.04947V2.94552ZM10.4148 7.16656L18.3637 9.82279C18.7687 9.95758 18.977 10.4047 18.8001 10.7564C18.7952 10.7613 18.7919 10.7646 18.7869 10.7696L17.2262 14.1359C17.2014 14.1901 17.1882 14.2477 17.1866 14.3068V17.8704C16.6344 18.301 15.7019 18.4753 15.1497 18.4753C14.5909 18.4753 13.6486 18.301 13.0997 17.8572C12.9426 17.7274 12.7145 17.7274 12.5574 17.8572C12.0085 18.301 11.0529 18.4753 10.4941 18.4753C9.93532 18.4753 8.99296 18.301 8.44407 17.8572C8.28701 17.7274 8.05886 17.7274 7.9018 17.8572C7.35291 18.301 6.39732 18.4753 5.83852 18.4753C5.27971 18.4753 4.33735 18.301 3.78847 17.8572C3.74548 17.8211 3.69588 17.7948 3.64298 17.7783V14.3068C3.64463 14.2427 3.62975 14.1803 3.6033 14.1227L2.00294 10.7696C1.84422 10.403 2.02608 9.95593 2.42617 9.82279L10.4148 7.16656ZM6.81725 10.5197C6.46676 10.5197 6.1824 10.9915 6.1824 11.5717C6.1824 12.1519 6.46676 12.6237 6.81725 12.6237C7.16775 12.6237 7.45211 12.1519 7.45211 11.5717C7.45211 10.9915 7.16775 10.5197 6.81725 10.5197ZM14.0123 10.5197C13.6618 10.5197 13.3774 10.9915 13.3774 11.5717C13.3774 12.1519 13.6618 12.6237 14.0123 12.6237C14.3628 12.6237 14.6471 12.1519 14.6471 11.5717C14.6471 10.9915 14.3628 10.5197 14.0123 10.5197ZM3.47104 19.4352C3.38672 19.4467 3.30902 19.4845 3.24619 19.5404C2.69731 19.9842 1.74172 20.1584 1.18291 20.1584C0.926657 20.1584 0.696853 20.119 0.429023 20.119C0.276923 20.1173 0.134742 20.1962 0.0570379 20.3277C-0.0190126 20.4592 -0.0190126 20.6203 0.0570379 20.7518C0.134742 20.8833 0.276923 20.9622 0.429023 20.9606C0.584431 20.9606 0.845647 21 1.18291 21C1.81942 21 2.7436 20.811 3.51072 20.3162C4.27949 20.8175 5.1987 21 5.83852 21C6.47503 21 7.39921 20.811 8.16632 20.3162C8.93509 20.8175 9.85431 21 10.4941 21C11.1306 21 12.0548 20.811 12.8219 20.3162C13.5907 20.8175 14.5099 21 15.1497 21C15.7862 21 16.7104 20.811 17.4775 20.3162C18.2463 20.8175 19.1655 21 19.8053 21C20.1426 21 20.4567 20.9507 20.7312 20.8422C20.951 20.7551 21.0569 20.5085 20.9692 20.2899C20.8816 20.0713 20.6336 19.9661 20.4137 20.0532C20.2649 20.1124 20.0616 20.1584 19.8053 20.1584C19.2465 20.1584 18.3042 19.9842 17.7553 19.5404C17.5982 19.4105 17.3701 19.4105 17.213 19.5404C16.6641 19.9842 15.7085 20.1584 15.1497 20.1584C14.5909 20.1584 13.6486 19.9842 13.0997 19.5404C12.9426 19.4105 12.7145 19.4105 12.5574 19.5404C12.0085 19.9842 11.0529 20.1584 10.4941 20.1584C9.93532 20.1584 8.99296 19.9842 8.44407 19.5404C8.28701 19.4105 8.05886 19.4105 7.9018 19.5404C7.35291 19.9842 6.39732 20.1584 5.83852 20.1584C5.27971 20.1584 4.33735 19.9842 3.78847 19.5404C3.71242 19.473 3.61322 19.4352 3.51072 19.4352C3.49749 19.4352 3.48426 19.4352 3.47104 19.4352Z"
                            fill="white"
                          />
                        </svg>
                      }
                    />
                  }
                  heading="Vessel tracking"
                  description="Global AIS network"
                />
                <CheckSwitch
                  action={() => toggleSelectedLayerType('ais')}
                  isActive={selectedLayerTypesSet.has('ais')}
                />
              </NavList.Item>
              <NavList.Item>
                <IconWithTextLockup
                  flex="1 1 0px"
                  image={
                    <MarkerGroupIcon
                      bg="#FF5C39"
                      icon={
                        <svg
                          width="19"
                          height="23"
                          viewBox="0 0 19 23"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.02976 0.00103123C7.82831 0.0387563 7.67811 0.211213 7.6622 0.417803C7.38123 3.61365 5.56464 5.72266 3.71801 7.90532C1.87137 10.088 0 12.3694 0 15.7952C0 18.1432 1.03376 19.9629 2.38914 21.1701C3.74451 22.3773 5.40383 22.9953 6.78571 22.9953C6.94829 22.9971 7.10026 22.9109 7.18331 22.7672C7.2646 22.6235 7.2646 22.4474 7.18331 22.3037C7.10026 22.16 6.94829 22.0737 6.78571 22.0755C5.6795 22.0755 4.17215 21.5402 2.98289 20.4803C1.79362 19.4204 0.904762 17.8719 0.904762 15.7952C0.904762 12.7072 2.57115 10.67 4.41071 8.49455C6.10891 6.48614 7.89546 4.32863 8.41146 1.25135C15.2626 6.13584 18.0952 11.1012 18.0952 14.8323C18.0952 18.3785 15.9659 21.233 13.8542 22.1043C13.6191 22.1995 13.506 22.469 13.5997 22.7079C13.6934 22.9468 13.9584 23.0618 14.1935 22.9666C16.7328 21.9193 19 18.7468 19 14.8323C19 10.5245 15.7909 5.20169 8.36905 0.0872598C8.28246 0.0243848 8.1782 -0.00615448 8.07217 0.00103123C8.05804 0.00103123 8.0439 0.00103123 8.02976 0.00103123ZM9.7686 7.08615C9.6449 7.10052 9.53358 7.16519 9.45759 7.26579C9.38337 7.36639 9.35333 7.49394 9.37277 7.61789C9.64844 9.23827 9.71559 10.5712 9.62723 11.57C9.53888 12.5688 9.29325 13.2335 9.03348 13.5389C8.77372 13.8443 8.5758 13.882 8.18527 13.7114C7.79474 13.5407 7.2434 13.0467 6.68676 12.1736C6.60724 12.0497 6.47294 11.9724 6.32626 11.967C6.18136 11.9617 6.04176 12.0281 5.95164 12.1449C4.26404 14.4587 4.34003 16.309 4.93378 17.5054C5.52753 18.7019 6.57366 19.2587 6.57366 19.2587C6.7168 19.3611 6.90411 19.3701 7.05785 19.2839C7.20982 19.1959 7.29818 19.027 7.28581 18.8492C7.27344 18.6713 7.16034 18.5186 6.99777 18.4539C6.99777 18.4539 6.19196 18.0138 5.73958 17.103C5.32961 16.2749 5.22182 15.0335 6.36161 13.2227C6.83873 13.8497 7.31938 14.3347 7.83185 14.5593C8.49451 14.8485 9.2473 14.6904 9.71205 14.1425C10.1768 13.5946 10.433 12.7503 10.532 11.6419C10.6009 10.8676 10.5461 9.90833 10.433 8.85383C11.6576 10.0844 12.6508 11.4101 13.0484 12.8491C13.5467 14.6527 13.2975 16.5641 11.8467 18.5833C11.6983 18.7899 11.7425 19.0791 11.9457 19.23C12.1489 19.3809 12.4334 19.336 12.5818 19.1294C14.1669 16.9234 14.4939 14.6581 13.9249 12.6048C13.3558 10.5515 11.928 8.71191 10.1079 7.18675C10.0125 7.1077 9.89053 7.07177 9.7686 7.08615Z"
                            fill="white"
                          />
                        </svg>
                      }
                    />
                  }
                  heading="Global heatmap"
                  description="Community route guidance"
                />
                <CheckSwitch
                  action={() =>
                    toggleSelectedLayerType('dynamicActivityHeatmap')
                  }
                  isActive={selectedLayerTypesSet.has('dynamicActivityHeatmap')}
                />
              </NavList.Item>
              <StyledNavListItem>
                <IconWithTextLockup
                  flex="1 1 0px"
                  image={
                    <Box position="relative">
                      <Box
                        position="absolute"
                        right="-6px"
                        top="-9px"
                        height="0px"
                      >
                        <SubscriberStarIcon width="18px" />
                      </Box>
                      <MarkerGroupIcon
                        bg="#001A70"
                        icon={
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              stroke="#ffffff"
                              d="M19.0328693,25 C18.9808057,25 18.9294011,24.9877513 18.8819507,24.9697006 C18.7442129,24.9084571 18.6545844,24.7769447 18.6545844,24.6318943 L18.6545844,22.7494624 L12.0286679,22.7494624 C9.80706813,22.7494624 8,20.9830708 8,18.8092487 C8,18.8034467 8,18.7267311 8.00131807,18.7189951 L8,18.691919 C8,16.5039142 9.80706813,14.7355886 12.029986,14.7355886 L19.9719911,14.7355886 C20.772057,14.7355886 21.4198863,14.1025242 21.4198863,13.321186 L21.4205453,13.2289984 C21.4198863,12.4083354 20.771398,11.773337 19.9713321,11.773337 L13.3473927,11.773337 L13.3473927,13.6325609 C13.3473927,13.78019 13.2584233,13.9110577 13.1206854,13.9723012 C12.9855837,14.0296766 12.8234616,14.0032452 12.712085,13.9020323 L9.27391054,10.7728115 C9.19746272,10.703832 9.15330752,10.6077764 9.15330752,10.5039848 C9.15330752,10.4008378 9.19680369,10.3028482 9.2732515,10.233224 L12.712085,7.09884593 C12.8234616,6.99827765 12.9855837,6.97249091 13.1206854,7.03051107 C13.2584233,7.09175458 13.3473927,7.22326695 13.3473927,7.36831735 L13.3473927,9.24945995 L19.9719911,9.24945995 C22.1929319,9.24945995 24,11.0171409 24,13.189029 L24,13.3102266 C24,15.4943634 22.1929319,17.2639784 19.9719911,17.2639784 L12.029986,17.2639784 C11.2305791,17.2639784 10.5820908,17.8976875 10.5820908,18.6770916 L10.5814317,18.7686346 C10.5820908,19.5899422 11.2305791,20.2268746 12.0286679,20.2268746 L18.6545844,20.2268746 L18.6545844,18.3657168 C18.6545844,18.219377 18.7442129,18.08722 18.8819507,18.0291998 C19.0190296,17.970535 19.1804926,17.9963217 19.2905511,18.0975347 L22.7300437,21.2254661 C22.8064915,21.295735 22.8493286,21.3950139 22.8493286,21.4988055 C22.8493286,21.5987292 22.8064915,21.6992974 22.7300437,21.7669876 L19.2905511,24.8994317 C19.2167394,24.9651879 19.1257929,25 19.0328693,25 Z"
                            />
                          </svg>
                        }
                      />
                    </Box>
                  }
                  heading="Underwater maps"
                  description="See beneath the surface"
                />
                <CheckSwitch
                  action={() => {
                    toggleSelectedLayerType('wmsbathymetry');
                  }}
                  isActive={selectedLayerTypesSet.has('wmsbathymetry')}
                />
              </StyledNavListItem>

              {mapSettingsMarkerGroups &&
                mapSettingsMarkerGroups.map((mapSettingsMarkerGroup) => {
                  const { displayName, menuIconNode, url } =
                    mapSettingsMarkerGroup;
                  return (
                    <NavList.Item
                      chevron
                      key={`menu-item_${displayName}`}
                      onClick={() => handleClickAction(url)}
                    >
                      <Block>{menuIconNode}</Block>
                      <Paragraph flex="auto">{displayName}</Paragraph>
                    </NavList.Item>
                  );
                })}
            </NavList>
          </CardBodyNoPadding>
        </Card>
      </SheetBody>
    </SheetLayout>
  );
};

MapSettingsContainerView.propTypes = {
  toggleSelectedLayerType: PropTypes.func.isRequired,
  BASE_MAP_LAYER_DEFINITIONS: PropTypes.objectOf(
    PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      iconImage: PropTypes.string.isRequired,
      iconBackgroundColor: PropTypes.string.isRequired,
    }),
  ).isRequired,
  handleUpdateSelectedBaseMapLayer: PropTypes.func.isRequired,

  selectedLayerTypes: PropTypes.arrayOf(PropTypes.string),
  selectedBaseMapLayer: PropTypes.string,
  mapSettingsMarkerGroups: PropTypes.arrayOf(PropTypes.shape({})),
};

MapSettingsContainerView.defaultProps = {
  selectedLayerTypes: [],
  selectedBaseMapLayer: null,
  mapSettingsMarkerGroups: [],
};

export default MapSettingsContainerView;
